import React from "react";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { getBrand } from "@/mock";

export function StarRating({ value = 5, size = 16, className = "" }) {
  return (
    <span className={`inline-flex items-center ${className}`} aria-label={`${value} out of 5 stars`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <Star
          key={i}
          style={{ width: size, height: size }}
          className={i < Math.round(value) ? "fill-amber-400 text-amber-400" : "text-neutral-300"}
          aria-hidden="true"
        />
      ))}
    </span>
  );
}

export function Breadcrumbs() {
  // Visible breadcrumb bar removed per client request (SEO breadcrumb JSON-LD is kept via Seo).
  return null;
}

export function ProductCard({ product }) {
  return (
    <Link
      to={`/products/${product.slug}`}
      data-testid={`product-card-${product.slug}`}
      className="group flex flex-col overflow-hidden rounded-xl border border-neutral-200 bg-white hover-lift focus-visible:ring-2 focus-visible:ring-[#D0021B]"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-neutral-100">
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {product.badge && (
          <span className="absolute left-3 top-3 rounded-full bg-[#D0021B] px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
            {product.badge}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
          {getBrand(product.brand)?.name || product.type}
        </p>
        <h3 className="mt-1 line-clamp-2 text-base font-semibold leading-snug text-neutral-900 group-hover:text-[#D0021B]">
          {product.name}
        </h3>
        <div className="mt-auto pt-3">
          <p className="text-lg font-bold text-[#D0021B]">
            {product.priceFrom ? "From " : ""}${product.price.toLocaleString()}
          </p>
          {product.compareAt && (
            <p className="text-sm font-medium text-neutral-400 line-through">
              {product.priceFrom ? "From " : ""}${product.compareAt.toLocaleString()}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}

export function SectionHeading({ eyebrow, title, center = false }) {
  return (
    <div className={center ? "text-center" : ""}>
      {eyebrow && (
        <p className="mb-1 text-sm font-bold uppercase tracking-widest text-[#D0021B]">
          {eyebrow}
        </p>
      )}
      <h2 className="text-3xl font-bold uppercase tracking-tight text-neutral-900 sm:text-4xl">
        {title}
      </h2>
    </div>
  );
}

export function PromoCard({ item }) {
  return (
    <Link
      to={item.to}
      data-testid={`promo-card-${item.brand.toLowerCase().replace(/\s+/g, "-")}`}
      className="group relative flex min-h-[300px] flex-col justify-between overflow-hidden rounded-3xl shadow-md transition-all duration-200 hover:-translate-y-1 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#D0021B] sm:min-h-[360px]"
    >
      {item.image && (
        <img
          src={item.image}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      )}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.2) 28%, rgba(0,0,0,0.55) 58%, rgba(0,0,0,0.9) 100%)",
        }}
        aria-hidden="true"
      />
      <span className="absolute inset-x-0 top-0 h-1.5" style={{ backgroundColor: item.accent }} aria-hidden="true" />

      {/* Logo badge */}
      <div className="relative p-5">
        {item.logo && (
          <span className="inline-flex items-center rounded-lg bg-white/95 px-3 py-1.5 shadow-sm">
            <img src={item.logo} alt={`${item.brand} logo`} loading="lazy" className="h-6 w-auto max-w-[120px] object-contain" />
          </span>
        )}
      </div>

      {/* Offer + CTA */}
      <div className="relative p-6 pt-0 text-white [text-shadow:0_1px_4px_rgba(0,0,0,0.65)]">
        {item.offerSmall && (
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/95">{item.offerSmall}</p>
        )}
        <p className="mt-1 text-2xl font-black uppercase leading-none sm:text-3xl">{item.offer}</p>
        {item.sub && <p className="mt-1.5 text-xs font-bold uppercase tracking-wide text-white">{item.sub}</p>}
        {item.note && <p className="mt-2 text-xs leading-snug text-white/90">{item.note}</p>}
        <span className="mt-4 inline-flex items-center rounded-full bg-[#D0021B] px-6 py-2.5 text-sm font-bold uppercase tracking-wide text-white [text-shadow:none] transition-colors group-hover:bg-[#a80016]">
          Shop Now
        </span>
      </div>
    </Link>
  );
}
